import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Globe, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  return (
    <>
      <Navbar />
      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center text-ta-green hover:underline">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center mb-6">
              <Shield className="h-8 w-8 text-ta-green mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            
            <div className="text-sm text-gray-500 mb-8">
              <p>Last Updated: June 15, 2025</p>
            </div>
            
            <div className="prose max-w-none text-gray-700">
              <p className="lead text-lg mb-6">
                At Travelcord, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
              
              <div className="bg-ta-green/5 p-4 rounded-lg mb-8 border-l-4 border-ta-green">
                <p className="font-medium">
                  Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
                </p>
              </div>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Database className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">Information We Collect</h2>
                </div>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">Personal Data</h3>
                <p className="mb-4">
                  We may collect personal identification information from Users in a variety of ways, including, but not limited to, when Users visit our site, register on the site, place an order, fill out a form, and in connection with other activities, services, features or resources we make available on our Site. Users may be asked for, as appropriate:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Mailing address</li>
                  <li>Payment information</li>
                  <li>Date of birth</li>
                  <li>Profile picture</li>
                  <li>Government-issued identification (for guides)</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">Non-Personal Data</h3>
                <p className="mb-4">
                  We may collect non-personal identification information about Users whenever they interact with our Site. Non-personal identification information may include:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Browser name</li>
                  <li>Type of computer or device</li>
                  <li>Technical information about Users' means of connection to our Site, such as the operating system and the Internet service providers utilized</li>
                  <li>Other similar information</li>
                </ul>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Eye className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">How We Use Your Information</h2>
                </div>
                
                <p className="mb-4">
                  Travelcord may collect and use Users' personal information for the following purposes:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>To provide and improve our services:</strong> We may use information to improve our products and services.</li>
                  <li><strong>To personalize user experience:</strong> We may use information in the aggregate to understand how our Users as a group use the services and resources provided on our Site.</li>
                  <li><strong>To process transactions:</strong> We may use the information Users provide about themselves when placing an order only to provide service to that order. We do not share this information with outside parties except to the extent necessary to provide the service.</li>
                  <li><strong>To facilitate bookings between travelers and guides:</strong> We share necessary information between travelers and guides to facilitate bookings and experiences.</li>
                  <li><strong>To send periodic emails:</strong> We may use the email address to send User information and updates pertaining to their order. It may also be used to respond to their inquiries, questions, and/or other requests.</li>
                  <li><strong>To send promotional communications:</strong> If User decides to opt-in to our mailing list, they will receive emails that may include company news, updates, related product or service information, etc. If at any time the User would like to unsubscribe from receiving future emails, we include detailed unsubscribe instructions at the bottom of each email.</li>
                </ul>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Lock className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">How We Protect Your Information</h2>
                </div>
                
                <p className="mb-4">
                  We adopt appropriate data collection, storage and processing practices and security measures to protect against unauthorized access, alteration, disclosure or destruction of your personal information, username, password, transaction information and data stored on our Site.
                </p>
                <p className="mb-4">
                  Sensitive and private data exchange between the Site and its Users happens over a SSL secured communication channel and is encrypted and protected with digital signatures. Our Site is also in compliance with PCI vulnerability standards in order to create as secure of an environment as possible for Users.
                </p>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <Globe className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">Sharing Your Personal Information</h2>
                </div>
                
                <p className="mb-4">
                  We do not sell, trade, or rent Users' personal identification information to others. We may share generic aggregated demographic information not linked to any personal identification information regarding visitors and users with our business partners, trusted affiliates and advertisers for the purposes outlined above.
                </p>
                <p className="mb-4">
                  We may use third party service providers to help us operate our business and the Site or administer activities on our behalf, such as sending out newsletters or surveys. We may share your information with these third parties for those limited purposes provided that you have given us your permission.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">Third-Party Service Providers</h3>
                <p className="mb-4">
                  We may share your information with the following third-party service providers:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Payment processors:</strong> To process payments for bookings and services.</li>
                  <li><strong>Cloud service providers:</strong> For hosting our website and storing data.</li>
                  <li><strong>Email service providers:</strong> To send communications and newsletters.</li>
                  <li><strong>Analytics providers:</strong> To understand how users interact with our site.</li>
                  <li><strong>Customer support software:</strong> To provide assistance and support.</li>
                </ul>
              </section>
              
              <section className="mb-8">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-5 w-5 text-ta-green mr-2" />
                  <h2 className="text-xl font-bold text-gray-900">Cookies and Tracking Technologies</h2>
                </div>
                
                <p className="mb-4">
                  Our Site may use "cookies" to enhance User experience. User's web browser places cookies on their hard drive for record-keeping purposes and sometimes to track information about them. User may choose to set their web browser to refuse cookies, or to alert you when cookies are being sent. If they do so, note that some parts of the Site may not function properly.
                </p>
                
                <h3 className="text-lg font-semibold mt-4 mb-2">Types of Cookies We Use</h3>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Essential cookies:</strong> Necessary for the operation of our website.</li>
                  <li><strong>Analytical/performance cookies:</strong> Allow us to recognize and count the number of visitors and see how visitors move around our website.</li>
                  <li><strong>Functionality cookies:</strong> Used to recognize you when you return to our website.</li>
                  <li><strong>Targeting cookies:</strong> Record your visit to our website, the pages you have visited and the links you have followed.</li>
                </ul>
              </section>
              
              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Your Rights</h2>
                
                <p className="mb-4">
                  Depending on your location, you may have certain rights regarding your personal information, including:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Right to access:</strong> You have the right to request copies of your personal information.</li>
                  <li><strong>Right to rectification:</strong> You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</li>
                  <li><strong>Right to erasure:</strong> You have the right to request that we erase your personal information, under certain conditions.</li>
                  <li><strong>Right to restrict processing:</strong> You have the right to request that we restrict the processing of your personal information, under certain conditions.</li>
                  <li><strong>Right to object to processing:</strong> You have the right to object to our processing of your personal information, under certain conditions.</li>
                  <li><strong>Right to data portability:</strong> You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</li>
                </ul>
                
                <p className="mb-4">
                  If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please contact us at our email: privacy@travelcord.com
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
                
                <p className="mb-4">
                  Our Site is not intended for children under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us so that we can take necessary actions.
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
                
                <p className="mb-4">
                  Travelcord has the discretion to update this privacy policy at any time. When we do, we will revise the updated date at the top of this page. We encourage Users to frequently check this page for any changes to stay informed about how we are helping to protect the personal information we collect. You acknowledge and agree that it is your responsibility to review this privacy policy periodically and become aware of modifications.
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Your Acceptance of These Terms</h2>
                
                <p className="mb-4">
                  By using this Site, you signify your acceptance of this policy. If you do not agree to this policy, please do not use our Site. Your continued use of the Site following the posting of changes to this policy will be deemed your acceptance of those changes.
                </p>
              </section>
              
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Us</h2>
                
                <p className="mb-4">
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium">Travelcord</p>
                  <p>Ntinda, Kampala, Uganda</p>
                  <p>Email: info@travelcord.guide</p>
                  <p>Phone: +256766830194</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;